'use client';

import { SelectOption } from '@repo/ui/components/multiselect/index';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import { Textarea } from '@repo/ui/components/ui/textarea';
import React, { useEffect } from 'react';
import ReactSelect from 'react-select';
import { FormField } from '../../../shared/components/form-field';
import { FormData } from '../../../shared/types';

interface TravauxStepProps {
  formData: FormData;
  errors: { [key: string]: string };
  onChange: (fieldName: string, value: any) => void;
  renderFreeforms: (fieldName: string) => React.ReactNode;
}

const ABATTAGE_OPTIONS: SelectOption<string>[] = [
  { value: 'aucun-abattage', label: 'Aucun abattage' },
  { value: 'abattage-simple', label: 'Abattage simple' },
  { value: 'abattage-en-vue-de-remplacement', label: 'Abattage en vue de remplacement' },
  { value: 'abattage-par-demontage', label: 'Abattage par démontage' },
];

// Field configurations for dynamic fields
const TRAVAUX_FIELDS = [
  {
    name: 'preco-travaux-collet',
    label: 'Système racinaire, collet',
    type: 'multiselect' as const,
    required: false,
    options: [
      { id: 'aucun-travaux', label: 'Aucun travaux' },
      { id: 'preservation-de-niche-ecologique', label: 'Préservation de niche écologique' },
      { id: 'coupe-des-rejets', label: 'Coupe des rejets' },
      { id: 'dessouchage', label: 'Dessouchage' },
      { id: 'mulchage-entretien-du-pied', label: 'Mulchage, entretien du pied' },
      { id: 'protection-du-pied', label: 'Protection du pied' },
    ]
  },
  {
    name: 'preco-travaux-tronc',
    label: 'Tronc',
    type: 'multiselect' as const,
    required: false,
    options: [
      { id: 'aucun-travaux', label: 'Aucun travaux' },
      { id: 'releve-de-couronne', label: 'Relevé de couronne' },
      { id: 'coupe-du-lierre', label: 'Coupe du lierre' },
      { id: 'mise-en-securite', label: 'Mise en sécurité' },
      { id: 'coupe-des-rejets', label: 'Coupe des rejets' },
      { id: 'taille-du-gui', label: 'Taille du gui' },
      { id: 'traitement-des-parasites', label: 'Traitement des parasites et des nuisibles' },
      { id: 'Préservation de niche écologique', label: 'Préservation de niche écologique' },
    ]
  },
  {
    name: 'preco-travaux-houppier',
    label: 'Houppier',
    type: 'multiselect' as const,
    required: false,
    options: [
      { "label": "Aucun travaux", "id": "aucun-travaux" },
      { "label": "Taille de cohabitation", "id": "taille-de-cohabitation" },
      { "label": "Taille de relevé de couronne", "id": "taille-de-relev-de-couronne" },
      { "label": "Taille d'allègement", "id": "taille-dallgement" },
      { "label": "Taille d'entretien", "id": "taille-dentretien" },
      {
        "label": "Taille d'éclaircie",
        "id": "taille-dclaircie"
      },
      {
        "label": "Nettoyage sanitaire",
        "id": "nettoyage-sanitaire"
      },
      {
        "label": "Mise en sécurité des branches à risque",
        "id": "mise-en-scurit-des-branches-risque"
      },
      { "label": "Taille du gui", "id": "taille-du-gui" },
      { "label": "Taille de gabarit", "id": "coupe-du-lierre" },
      { "label": "Taille travaillée", "id": "taille-travaille" },
      { "label": "Taille d'accompagnement", "id": "taille-daccompagnement" },
      { "label": "Taille de restructuration", "id": "taille-de-restructuration" },
      { "label": "Taille de formation", "id": "taille-de-formation" },
      { "label": "Conduite en trogne", "id": "conduite-en-trogne-ttard" },
      { "label": "Conduite architecturée", "id": "conduite-architecturee" },
      { "label": "Préservation de niche écologique", "id": "prservation-de-niche-cologique" },
      { "label": "Traitement des nuisibles, parasites", "id": "traitement-des-nuisibles" },
      { "label": "Haubanage", "id": "haubanage" }

    ]
  }
];

const ECHEANCE_OPTIONS: SelectOption<string>[] = [
  { value: 'immediat', label: 'Dès que possible' },
  { value: '1an', label: 'Moins d\'1 an' },
  { value: '2ans', label: 'Moins de 2 ans' },
  { value: '3ans', label: 'Moins de 3 ans' },
  { value: '5ans', label: '5 ans et plus' },
];

export function TravauxStep({ formData, errors, onChange, renderFreeforms }: TravauxStepProps) {
  const selectedAbattage = formData['preco-travaux-abattage'];
  const selectedEcheance = formData['preco-travaux-date-travaux'];
  const selectedProchaineVisite = formData['preco-travaux-prochaine-visite'];
  const commentaires = formData['preco-travaux-com'] || '';

  // Calculate default date for prochaine visite based on échéance selection
  const calculateDefaultDate = (echeance: string): any => {
    if (!echeance) return '';

    const today = new Date();
    let targetDate = new Date(today);

    switch (echeance) {
      case 'immediat':
        targetDate.setDate(today.getDate() + 7); // 1 week from now
        break;
      case '1an':
        targetDate.setFullYear(today.getFullYear() + 1);
        break;
      case '2ans':
        targetDate.setFullYear(today.getFullYear() + 2);
        break;
      case '3ans':
        targetDate.setFullYear(today.getFullYear() + 3);
        break;
      case '5ans':
        targetDate.setFullYear(today.getFullYear() + 5);
        break;
      default:
        return '';
    }

    return targetDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  // Calculate échéance based on a given date (inverse function)
  const calculateEcheanceFromDate = (dateString: string): string => {
    if (!dateString) return '';

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Calculate difference in years
    const diffYears = targetDate.getFullYear() - today.getFullYear();
    const diffMonths = (targetDate.getFullYear() - today.getFullYear()) * 12 + (targetDate.getMonth() - today.getMonth());

    // Determine échéance based on date
    if (diffDays <= 7) {
      return 'immediat';
    } else if (diffMonths < 12) {
      return '1an';
    } else if (diffMonths < 24) {
      return '2ans';
    } else if (diffMonths < 36) {
      return '3ans';
    } else {
      return '5ans';
    }
  };

  // Get the current prochaine visite value or calculate default
  const getProchaineVisiteValue = (): string => {
    const currentValue = formData['preco-travaux-prochaine-visite'];
    if (currentValue) return currentValue; // User has already set a value

    // Calculate default based on échéance
    return calculateDefaultDate(selectedProchaineVisite || '') || '';
  };

  const isNotSelectedAbattage = !selectedAbattage || selectedAbattage === 'aucun-abattage';
  return (
    <div className="space-y-4">
      {/* Abattage Field */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Abattage
        </Label>
        <ReactSelect<SelectOption<string>, false>
          options={ABATTAGE_OPTIONS}
          value={ABATTAGE_OPTIONS.find(opt => opt.value === selectedAbattage) || null}
          onChange={(newValue) => onChange('preco-travaux-abattage', newValue?.value || '')}
          placeholder="Sélectionnez le type d'abattage"
          isSearchable={false}
          isClearable={true}
          className="react-select-container"
          classNamePrefix="react-select"
          styles={{
            control: (base: any) => ({
              ...base,
              minHeight: '40px',
              border: '2px solid hsl(var(--input))',
              '&:hover': {
                border: '2px solid hsl(var(--primary) / 0.2)',
              },
            }),
            placeholder: (base: any) => ({
              ...base,
              color: 'hsl(var(--muted-foreground))',
            }),
          }}
        />
        {errors['preco-travaux-abattage'] && (
          <p className="text-sm text-red-500">{errors['preco-travaux-abattage']}</p>
        )}
      </div>

      {/* Dynamic Travaux Fields */}
      {isNotSelectedAbattage && TRAVAUX_FIELDS.map((field) => (
        <FormField
          key={field.name}
          field={field}
          value={formData[field.name] || []}
          error={errors[field.name]}
          onChange={onChange}
          renderFreeforms={renderFreeforms}
        />
      ))}
      {/* Échéance Field */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Date prévisionnelle des travaux
        </Label>
        <ReactSelect<SelectOption<string>, false>
          options={ECHEANCE_OPTIONS}
          value={ECHEANCE_OPTIONS.find(opt => opt.value === selectedEcheance) || null}
          onChange={(newValue) => {
            onChange('preco-travaux-date-travaux', newValue?.value || '')
            onChange('_userEditedDateTravaux', calculateDefaultDate(newValue?.value || ''))
          }}
          placeholder="Sélectionnez l'échéance"
          isSearchable={false}
          isClearable={true}
          className="react-select-container"
          classNamePrefix="react-select"
          styles={{
            control: (base: any) => ({
              ...base,
              minHeight: '40px',
              border: '2px solid hsl(var(--input))',
              '&:hover': {
                border: '2px solid hsl(var(--primary) / 0.2)',
              },
            }),
            placeholder: (base: any) => ({
              ...base,
              color: 'hsl(var(--muted-foreground))',
            }),
          }}
        />
        {errors['preco-travaux-date-travaux'] && (
          <p className="text-sm text-red-500">{errors['preco-travaux-date-travaux']}</p>
        )}
      </div>

      {/* Prochaine Visite Field */}
      <div className="space-y-2">
        <Label htmlFor="preco-travaux-date-travaux" className="text-sm font-medium">
          Date saisie par l'utilisateur (travaux)
        </Label>
        <Input
          id="preco-travaux-date-travaux"
          type="date"
          value={formData['_userEditedDateTravaux'] ? new Date(formData['_userEditedDateTravaux']).toISOString().split('T')[0] : ''}
          onChange={(e) => {
            const selectedDate = e.target.value;
            onChange('_userEditedDateTravaux', selectedDate);
            // Update échéance based on selected date
            const calculatedEcheance = calculateEcheanceFromDate(selectedDate);
            if (calculatedEcheance) {
              onChange('preco-travaux-date-travaux', calculatedEcheance);
            }
          }}
          className={errors['preco-travaux-prochaine-visite'] ? 'border-red-500' : ''}
        />
        <p className="text-xs text-muted-foreground">
          Date recalculée automatiquement selon l'échéance. Vous pouvez la modifier manuellement.
        </p>
        {errors['preco-travaux-prochaine-visite'] && (
          <p className="text-sm text-red-600">{errors['preco-travaux-prochaine-visite']}</p>
        )}
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Prochaine visite
        </Label>
        <ReactSelect<SelectOption<string>, false>
          options={ECHEANCE_OPTIONS}
          value={ECHEANCE_OPTIONS.find(opt => opt.value === selectedProchaineVisite) || null}
          onChange={(newValue) => {
            onChange('preco-travaux-prochaine-visite', newValue?.value || '')
            onChange('_userEditedDateProVisite', calculateDefaultDate(newValue?.value || ''))
          }}
          placeholder="Sélectionnez l'échéance"
          isSearchable={false}
          isClearable={true}
          className="react-select-container"
          classNamePrefix="react-select"
          styles={{
            control: (base: any) => ({
              ...base,
              minHeight: '40px',
              border: '2px solid hsl(var(--input))',
              '&:hover': {
                border: '2px solid hsl(var(--primary) / 0.2)',
              },
            }),
            placeholder: (base: any) => ({
              ...base,
              color: 'hsl(var(--muted-foreground))',
            }),
          }}
        />
        {errors['preco-travaux-prochaine-visite'] && (
          <p className="text-sm text-red-500">{errors['preco-travaux-prochaine-visite']}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="preco-travaux-date-pro-visite" className="text-sm font-medium">
          Date saisie par l'utilisateur (prochaine visite)
        </Label>
        <Input
          id="preco-travaux-date-pro-visite"
          type="date"
          value={formData['_userEditedDateProVisite'] ? new Date(formData['_userEditedDateProVisite']).toISOString().split('T')[0] : ''}
          onChange={(e) => {
            const selectedDate = e.target.value;
            onChange('_userEditedDateProVisite', selectedDate);
            // Update échéance based on selected date
            const calculatedEcheance = calculateEcheanceFromDate(selectedDate);
            if (calculatedEcheance) {
              onChange('preco-travaux-prochaine-visite', calculatedEcheance);
            }
          }}
          className={errors['preco-travaux-prochaine-visite'] ? 'border-red-500' : ''}
        />
        <p className="text-xs text-muted-foreground">
          Date recalculée automatiquement selon l'échéance. Vous pouvez la modifier manuellement.
        </p>
        {errors['preco-travaux-prochaine-visite'] && (
          <p className="text-sm text-red-600">{errors['preco-travaux-prochaine-visite']}</p>
        )}
      </div>

      {/* Commentaires Field */}
      <div className="space-y-2">
        <Label htmlFor="preco-travaux-com" className="text-sm font-medium">
          Commentaires
        </Label>
        <Textarea
          id="preco-travaux-com"
          value={commentaires}
          onChange={(e) => onChange('preco-travaux-com', e.target.value)}
          placeholder="Ajoutez vos commentaires..."
          className={errors['preco-travaux-com'] ? 'border-red-500' : ''}
        />
        {errors['preco-travaux-com'] && (
          <p className="text-sm text-red-600">{errors['preco-travaux-com']}</p>
        )}
      </div>
    </div>
  );
}
